/**
 * Geocoding service using OpenStreetMap Nominatim API.
 * Free, no API key required. Respects usage policy: max 1 req/sec, custom User-Agent.
 */

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
const USER_AGENT = 'ObjectLink/1.0';
const MIN_INTERVAL_MS = 1100; // Nominatim requires max 1 req/sec

let lastRequestTime = 0;

async function throttledFetch(url) {
  const now = Date.now();
  const elapsed = now - lastRequestTime;
  if (elapsed < MIN_INTERVAL_MS) {
    await new Promise(resolve => setTimeout(resolve, MIN_INTERVAL_MS - elapsed));
  }
  lastRequestTime = Date.now();

  const response = await fetch(url, {
    headers: { 'User-Agent': USER_AGENT }
  });
  if (!response.ok) {
    throw new Error(`Geocoding request failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Forward geocoding: search for an address and return matching locations.
 * @param {string} query - Address or place name to search for
 * @param {number} limit - Max results (default 5)
 * @returns {Promise<Array<{lat: number, lng: number, displayName: string, type: string}>>}
 */
export async function searchAddress(query, limit = 5) {
  if (!query || query.trim().length < 3) return [];
  
  const params = new URLSearchParams({
    q: query.trim(),
    format: 'json',
    limit: String(limit),
    addressdetails: '1',
    'accept-language': 'de'
  });

  const results = await throttledFetch(`${NOMINATIM_BASE}/search?${params}`);
  
  return results.map(r => ({
    lat: parseFloat(r.lat),
    lng: parseFloat(r.lon),
    displayName: r.display_name,
    type: r.type,
    category: r.category,
    address: r.address
  }));
}

/**
 * Reverse geocoding: get address for coordinates.
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{displayName: string, address: object}|null>}
 */
export async function reverseGeocode(lat, lng) {
  if (lat == null || lng == null) return null;

  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lng),
    format: 'json',
    addressdetails: '1',
    'accept-language': 'de',
    zoom: '18'
  });

  const result = await throttledFetch(`${NOMINATIM_BASE}/reverse?${params}`);
  
  if (result.error) return null;

  return {
    displayName: result.display_name,
    address: result.address
  };
}

/**
 * Format a Nominatim address object into a short readable string.
 * Prefers: Street HouseNumber, City
 */
export function formatShortAddress(address) {
  if (!address) return '';
  const parts = [];
  
  const street = address.road || address.pedestrian || address.footway || '';
  if (street) {
    parts.push(address.house_number ? `${street} ${address.house_number}` : street);
  }
  
  const city = address.city || address.town || address.village || address.municipality || '';
  if (city) parts.push(city);
  
  if (parts.length === 0) {
    // Fallback: use county or state
    const fallback = address.county || address.state || address.country || '';
    if (fallback) parts.push(fallback);
  }
  
  return parts.join(', ');
}

// Debounce helper for search input
let debounceTimer = null;
export function debouncedSearchAddress(query, callback, delay = 400) {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    try {
      const results = await searchAddress(query);
      callback(results, null);
    } catch (error) {
      callback([], error);
    }
  }, delay);
}
